import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useBackTarget } from '../../hooks/useBackTarget';

const font = "'Inria Serif', serif";
const SUPPORT_EMAIL = 'contact@allurexp.com';
const LAST_UPDATED = 'February 3, 2026';

const COMPANY = {
  name: 'ALLURE EXPERIENCE I.K.E.',
  nameLatin: 'ALLURE EXPERIENCE P.C.',
  distinctive: 'ALLUREXP',
  legalForm: 'IKE (Private Company / Ιδιωτική Κεφαλαιουχική Εταιρεία)',
  gemi: '194817609000',
  euid: 'ELGEMI.194817609000',
  chamber: 'Επαγγελματικό Επιμελητήριο Πειραιώς',
  vat: 'EL803335274',
  taxOffice: 'ΚΕΦΟΔΕ ΑΤΤΙΚΗΣ',
  address: 'Εμμανουήλ Δημελά 74, 16674 Αθήνα, Ελλάδα',
  addressLatin: 'Emmanouil Dimela 74, 16674 Athens, Greece',
  phone: '+30 694 396 3291',
  administrator: 'Horatiu Marian Onofrei',
  website: 'https://allurexp.com',
  incorporatedOn: '01 July 2026',
  capital: '€1,000.00',
};

const sectionStyle = { marginTop: 28 };
const h2Style = { fontFamily: font, fontSize: 22, fontWeight: 700, color: '#111', margin: '0 0 10px' };
const pStyle = { fontFamily: font, fontSize: 15, lineHeight: 1.6, color: '#333', margin: '0 0 10px' };
const rowStyle = { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12, padding: '8px 0', borderBottom: '1px solid #ececec' };
const labelStyle = { fontFamily: font, fontSize: 14, fontWeight: 700, color: '#666' };
const valueStyle = { fontFamily: font, fontSize: 15, color: '#111' };

function Row({ label, children, testid }) {
  return (
    <div style={rowStyle} data-testid={testid}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{children}</div>
    </div>
  );
}

export default function LegalImprintPage() {
  const goBack = useBackTarget();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="legal-imprint-page"
      style={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        padding: '20px 20px 80px',
        maxWidth: 820,
        margin: '0 auto',
      }}
    >
      <button
        type="button"
        onClick={goBack}
        data-testid="imprint-back-button"
        aria-label="Back"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: 'none',
          background: 'transparent',
          fontFamily: font,
          fontSize: 15,
          color: '#703FF6',
          cursor: 'pointer',
          padding: 0,
          marginBottom: 14,
        }}
      >
        <ArrowLeft size={18} /> Back
      </button>

      <h1
        style={{
          fontFamily: font,
          fontSize: 30,
          fontWeight: 700,
          color: '#111',
          margin: 0,
        }}
      >
        Legal Notice / Imprint
      </h1>
      <p style={{ ...pStyle, color: '#666', marginTop: 8 }}>
        Last updated: {LAST_UPDATED}
      </p>

      <section style={sectionStyle}>
        <p style={pStyle}>
          Information provided in accordance with Greek Law 4548/2018, EU Directive 2000/31/EC on
          electronic commerce, and Article 5 of the German Telemedia Act (§5 TMG), where
          applicable to visitors from those jurisdictions.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Company details</h2>
        <div>
          <Row label="Legal name (Greek)" testid="imprint-row-name">{COMPANY.name}</Row>
          <Row label="Legal name (Latin)" testid="imprint-row-name-latin">{COMPANY.nameLatin}</Row>
          <Row label="Distinctive title" testid="imprint-row-distinctive">{COMPANY.distinctive}</Row>
          <Row label="Legal form" testid="imprint-row-form">{COMPANY.legalForm}</Row>
          <Row label="Date of incorporation" testid="imprint-row-incorp">{COMPANY.incorporatedOn}</Row>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Registration</h2>
        <div>
          <Row label="Γ.Ε.ΜΗ. (GEMI)" testid="imprint-row-gemi">{COMPANY.gemi}</Row>
          <Row label="EUID" testid="imprint-row-euid">{COMPANY.euid}</Row>
          <Row label="Chamber" testid="imprint-row-chamber">{COMPANY.chamber}</Row>
          <Row label="VAT / ΑΦΜ" testid="imprint-row-vat">{COMPANY.vat}</Row>
          <Row label="Tax office (ΔΟΥ)" testid="imprint-row-doy">{COMPANY.taxOffice}</Row>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Registered seat</h2>
        <div>
          <Row label="Address (Greek)" testid="imprint-row-address">{COMPANY.address}</Row>
          <Row label="Address (Latin)" testid="imprint-row-address-latin">{COMPANY.addressLatin}</Row>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Legal representation</h2>
        <div>
          <Row label="Administrator (Διαχειριστής)" testid="imprint-row-admin">
            {COMPANY.administrator}
          </Row>
          <Row label="Authority" testid="imprint-row-auth">
            Sole signature — legally binds the company individually
          </Row>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Partners (Εταίροι)</h2>
        <div>
          <Row label="Partner" testid="imprint-row-partner-1">
            Katerina Louiza Kapetaniou
          </Row>
          <Row label="Partner" testid="imprint-row-partner-2">
            Horatiu Marian Onofrei
          </Row>
        </div>
        <p style={{ ...pStyle, marginTop: 10, color: '#666', fontSize: 13 }}>
          Full ownership details (share allocation, tax identifiers) are publicly available via
          the Greek General Commercial Registry (Γ.Ε.ΜΗ.) under number {COMPANY.gemi}.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Contact</h2>
        <div>
          <Row label="Email" testid="imprint-row-email">
            <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#703FF6' }}>{SUPPORT_EMAIL}</a>
          </Row>
          <Row label="Phone" testid="imprint-row-phone">{COMPANY.phone}</Row>
          <Row label="Website" testid="imprint-row-website">
            <a href={COMPANY.website} style={{ color: '#703FF6' }}>{COMPANY.website}</a>
          </Row>
        </div>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Consumer dispute resolution (EU ODR)</h2>
        <p style={pStyle}>
          The European Commission provides an online dispute resolution platform (ODR) available
          at{' '}
          <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" style={{ color: '#703FF6' }}>
            https://ec.europa.eu/consumers/odr
          </a>
          . We are not obliged to, and do not currently, participate in alternative dispute
          resolution proceedings before a consumer arbitration board.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>Related legal documents</h2>
        <p style={pStyle}>
          Please also read our{' '}
          <Link to="/privacy" style={{ color: '#703FF6' }} data-testid="imprint-privacy-link">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link to="/terms" style={{ color: '#703FF6' }} data-testid="imprint-terms-link">
            Terms of Service
          </Link>
          . Signed venue and host agreements are available inside your Allure dashboard.
        </p>
      </section>
    </div>
  );
}
